<template>
  <div class="setup-portal">
    <header class="setup-header">
      <Stack
        class="setup-header-title"
        gap="0"
      >
        <Title size="4_5">
          <p>Client Portal Setup</p>
        </Title>
        <p
          v-if="clientPortalStore.portalContext?.client"
          class="setup-header-name"
        >
          For
          <router-link
            :to="{
              name: 'entity-detail',
              params: { entityExternalId: clientPortalStore.portalContext?.client.external_id }
            }"
            target="_blank"
            class="tw-inline-flex tw-items-center"
          >
            {{ clientPortalStore.portalContext?.client.name }}
            <Icon
              icon="fas fa-external-link-alt"
              size="3"
            />
          </router-link>
        </p>
      </Stack>

      <nav class="setup-header-nav">
        <Pills class="setup-header-pills">
          <PillsList class="setup-header-pills-list">
            <PillItem
              v-for="pill in pills"
              :key="pill.label"
              class="setup-nav-item"
              :icon="pill.icon"
              :to="pill.to"
            >
              {{ pill.label }}
            </PillItem>
          </PillsList>
        </Pills>
      </nav>
    </header>

    <router-view :context="context" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Icon, Pills, PillsList, PillItem, Stack, Title } from '@stakeui';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';

const clientPortalStore = useClientPortalStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
  firmSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorView: {
    type: Boolean,
    default: false,
  },
});

const context = ref({});

const pills = computed(() => {
  const params = {
    firmSlug: props.firmSlug,
    advisorSlug: props.advisorSlug,
    entityExternalId: props.entityExternalId,
  };

  return [
    {
      label: 'Configure',
      icon: 'fas fa-tasks',
      to: { name: 'advisor-client-portal-setup-welcome', params },
    },
    {
      label: 'Preview Portal',
      icon: 'fas fa-eye',
      to: { name: 'advisor-client-portal-setup-preview', params },
    },
    {
      label: 'Send link to Client',
      icon: 'fas fa-link',
      to: { name: 'advisor-client-portal-setup-link', params },
    },
  ];
},
);

// const fetchPortalContext = async (firmSlug, advisorSlug, entityExternalId) => {
//   isFetching.value = true;

//   const url = `/api/v1/client-portal/context/${firmSlug}/${advisorSlug}/${entityExternalId}`;

//   try {
//     const response = await fetch(
//         url, { credentials: 'include' },
//     );

//     if (!response.ok) {
//       throw new Error(`Failed to fetch context: ${response.status}`);
//     }

//     const data = await response.json();
//     context.value = data;
//   } catch (err) {
//     console.error(`Error fetching context: ${err.message}`);
//   } finally {
//     isFetching.value = false;
//     hasFetched.value = true;
//   }
// };

onMounted(async () => {
  await clientPortalStore.fetchConfigurationData(
      props.firmSlug,
      props.advisorSlug,
      props.entityExternalId,
  );
});
</script>

<style scoped>
.setup-portal {
  --spacing: 1.5em;
  --box-inner-shadow: 0 -1px 0 0 rgba(0, 0, 0, 0.10) inset, 0 1px 0 0 rgba(255, 255, 255, 0.10) inset;
  --page-background: #F3F4F6;
  --center-width: 45em;
  --center-padding: 2em;
  --center-max-width: calc(calc(2 * var(--center-padding)) + var(--center-width));
  background-color: var(--page-background);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.setup-header {
  background-color: #fff;
  padding: var(--spacing) 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2em;
  text-align: center;
  box-shadow: var(--box-inner-shadow);
  flex-shrink: 0;
}

.setup-header-name {
  color: var(--stk-text-normal);
  font-size: 0.875em;
}

.setup-header-nav {
  width: 20em;
}

.setup-header-pills {
  padding: 0.25em;
  border-radius: 0.625em;
}

.setup-header-pills-list {
  width: 100%;
}

.setup-nav-item {
  width: 100%;
}

.setup-header-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
}

.setup-main {
  display: flex;
  justify-content: center;
  padding: var(--spacing);
  padding-bottom: calc(2 * var(--spacing));
}

.setup-main-body {
  flex-grow: 1;
  max-width: var(--center-width);
  width: 100%;
}

@media screen and (min-width: 920px) {
  .setup-header {
    display: grid;
    grid-template-columns: 18em 1fr;
    justify-items: start;
  }

  .setup-header-title {
    text-align: left;
    padding-left: 2em;
  }

  .setup-header-nav {
    width: 45em;
  }

  .setup-nav-item {
    width: 33.3333%;
  }
}
</style>

<style>
.floating-chat-button,
.footer {
  display: none;
}
</style>
